import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Button } from "./ui/button"
import { AlertCircle, Home, RefreshCcw } from "lucide-react"

export default function ErrorPage() {
    const { t } = useTranslation()
    const error = useRouteError()
    const navigate = useNavigate()

    let errorMessage: string
    let errorTitle: string
    let errorStatus: number | undefined

    if (isRouteErrorResponse(error)) {
        errorStatus = error.status
        if (error.status === 404) {
            errorTitle = t("errors.pageNotFound")
            errorMessage = t("errors.pageNotFoundDesc")
        } else if (error.status === 401) {
            errorTitle = t("errors.unauthorized")
            errorMessage = t("errors.unauthorizedDesc")
        } else if (error.status === 503) {
            errorTitle = t("errors.serviceUnavailable")
            errorMessage = t("errors.serviceUnavailableDesc")
        } else {
            errorTitle = t("errors.genericTitle")
            errorMessage = error.statusText || t("errors.generic")
        }
    } else if (error instanceof Error) {
        const isChunkLoadFailed = error.message && (
            error.message.includes('Failed to fetch dynamically imported module') ||
            error.message.includes('Importing a module script failed')
        );

        if (isChunkLoadFailed) {
            const RELOAD_KEY = 'creatix_chunk_reload';
            const hasReloaded = sessionStorage.getItem(RELOAD_KEY);

            if (!hasReloaded) {
                sessionStorage.setItem(RELOAD_KEY, 'true');
                window.location.reload();
                return null;
            } else {
                sessionStorage.removeItem(RELOAD_KEY);
                errorTitle = t("errors.updateAvailable")
                errorMessage = t("errors.hardRefreshRequired")
            }
        } else {
            errorTitle = t("errors.appError")
            errorMessage = error.message || t("errors.generic")
        }
    } else {
        errorTitle = t("errors.unknown")
        errorMessage = t("errors.unknownDesc")
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background text-foreground animate-in fade-in duration-500">
            <div className="w-full max-w-md text-center space-y-6">
                <div className="flex justify-center">
                    <div className="h-24 w-24 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
                        <AlertCircle className="h-12 w-12 text-destructive" />
                    </div>
                </div>

                <div className="space-y-2">
                    <h1 className="text-3xl font-bold tracking-tight">{errorTitle}</h1>
                    <p className="text-muted-foreground text-lg">{errorMessage}</p>
                    {errorStatus && (
                        <p className="text-sm text-muted-foreground font-mono mt-2">{t("errors.errorCode", { status: errorStatus })}</p>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                    <Button
                        variant="default"
                        size="lg"
                        onClick={() => navigate("/")}
                        className="flex items-center gap-2"
                    >
                        <Home className="h-4 w-4" />
                        {t("eventDetail.goHome")}
                    </Button>

                    <Button
                        variant="outline"
                        size="lg"
                        onClick={() => window.location.reload()}
                        className="flex items-center gap-2"
                    >
                        <RefreshCcw className="h-4 w-4" />
                        {t("errors.refreshPage")}
                    </Button>
                </div>
            </div>
        </div>
    )
}
